/**
 * Interlocked meal counts for the Complete-service wizard.
 *
 * The four counts a school enters are not independent — two identities hold:
 *
 *   1. `served === reimbursable + nonReimbursable`
 *      Every meal passed out is either a student's first serving (reimbursable)
 *      or a second serving / staff meal (non-reimbursable).
 *   2. `served + leftover === planned`
 *      What Ordo provisioned either went out or came back.
 *
 * Typing one number therefore implies others. This module owns that inference so
 * the wizard never re-derives it inline (which is how the `served`-driven branch
 * came to compute `nonReimbursable = served - served === 0` forever).
 *
 * Three rules make the behavior predictable for a worker standing in a
 * cafeteria:
 *
 * - **The edited field is never overwritten.** Whatever they just typed stands.
 * - **A field they typed earlier is never auto-adjusted again** (the `manual`
 *   set). Derivation only ever moves numbers nobody has asserted.
 * - **A conflict is reported, never silently "fixed".** When honoring the
 *   identity would mean overwriting a manual entry, we leave the numbers alone
 *   and return a violation for the UI to surface. Silently rewriting a worker's
 *   count on a federal compliance record is worse than showing a warning.
 *
 * `REIMBURSABLE_EXCEEDS_SERVED` is a HARD violation: the server rejects that
 * state outright (`saveProductionRecordQuantities` throws `invalid_quantities`),
 * so the wizard must block submit on it rather than let the worker discover it
 * as an opaque failure at the end of the flow. `SERVED_EXCEEDS_PLANNED` is soft
 * — over-service is real (walk-ins, seconds) and must not block.
 */

/** The four interlocked counts for one deliverable × grade-group row. */
export interface ProductionCounts {
  served: number
  reimbursable: number
  nonReimbursable: number
  leftover: number
}

export type CountField = keyof ProductionCounts

export type CountViolation =
  /** `reimbursable + nonReimbursable > served` — the server rejects this. Blocks submit. */
  | { kind: 'REIMBURSABLE_EXCEEDS_SERVED'; excess: number }
  /** A manual split no longer reaches `served`, and the field that would absorb it is also manual. */
  | { kind: 'SPLIT_BELOW_SERVED'; shortfall: number }
  /** More served than provisioned. Legitimate; warn only. */
  | { kind: 'SERVED_EXCEEDS_PLANNED'; excess: number }

export interface DeriveInput {
  current: ProductionCounts
  field: CountField
  value: number
  /** Ordo-provisioned count for the row; drives the leftover identity. */
  planned: number
  /** Fields the worker has typed directly — never auto-adjusted. */
  manual: ReadonlySet<CountField>
}

export interface DeriveResult {
  counts: ProductionCounts
  violations: CountViolation[]
}

/** Violations that must block submit (the server would reject the save). */
export function blocksSubmit(violation: CountViolation): boolean {
  return violation.kind === 'REIMBURSABLE_EXCEEDS_SERVED' || violation.kind === 'SPLIT_BELOW_SERVED'
}

/** Seed for an unworked row: everything provisioned went out as first servings. */
export function seedProductionCounts(planned: number): ProductionCounts {
  const served = clamp(planned)
  return { served, reimbursable: served, nonReimbursable: 0, leftover: 0 }
}

function clamp(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0
}

/**
 * Apply one edit and re-derive the rest of the row.
 *
 * A non-finite `value` (empty input, NaN) is a no-op: the row is returned
 * unchanged with no violations, so clearing a box mid-typing never cascades.
 */
export function deriveProductionCounts(input: DeriveInput): DeriveResult {
  const { current, field, planned, manual } = input
  if (!Number.isFinite(input.value)) {
    return { counts: { ...current }, violations: [] }
  }

  const next: ProductionCounts = { ...current, [field]: clamp(input.value) }
  // The edited field is authoritative for this pass, whatever the manual set says.
  const locked = (f: CountField) => f === field || manual.has(f)

  switch (field) {
    case 'served':
      applyLeftoverFromServed(next, planned, locked)
      rebalanceSplit(next, locked)
      break

    case 'leftover':
      // Leftovers came back, so they did NOT go out: served drops to match.
      if (!locked('served')) {
        next.served = clamp(planned - next.leftover)
        rebalanceSplit(next, locked)
      }
      break

    case 'nonReimbursable':
      // Served is held constant — seconds/staff meals come OUT of the
      // reimbursable count, they do not add to the total passed out.
      rebalanceSplit(next, locked)
      break

    case 'reimbursable':
      rebalanceSplit(next, locked)
      break
  }

  return { counts: next, violations: collectViolations(next, planned) }
}

function applyLeftoverFromServed(next: ProductionCounts, planned: number, locked: (f: CountField) => boolean): void {
  if (!locked('leftover')) {
    next.leftover = clamp(planned - next.served)
  }
}

/**
 * Restore `reimbursable + nonReimbursable === served` by moving whichever of the
 * two nobody has asserted. Reimbursable absorbs first: it is the derived default
 * (first servings), while a typed non-reimbursable is a deliberate statement
 * about seconds and staff meals.
 */
function rebalanceSplit(next: ProductionCounts, locked: (f: CountField) => boolean): void {
  if (next.reimbursable + next.nonReimbursable === next.served) {
    return
  }
  if (!locked('reimbursable')) {
    next.reimbursable = clamp(next.served - next.nonReimbursable)
    return
  }
  if (!locked('nonReimbursable')) {
    next.nonReimbursable = clamp(next.served - next.reimbursable)
  }
  // Both locked: leave the worker's numbers alone — collectViolations reports it.
}

function collectViolations(counts: ProductionCounts, planned: number): CountViolation[] {
  const violations: CountViolation[] = []
  const split = counts.reimbursable + counts.nonReimbursable
  if (split > counts.served) {
    violations.push({ kind: 'REIMBURSABLE_EXCEEDS_SERVED', excess: split - counts.served })
  } else if (split < counts.served) {
    violations.push({ kind: 'SPLIT_BELOW_SERVED', shortfall: counts.served - split })
  }
  if (counts.served > planned) {
    violations.push({ kind: 'SERVED_EXCEEDS_PLANNED', excess: counts.served - planned })
  }
  return violations
}

/** Human-readable warning for a violation, shown inline under the row. */
export function countViolationMessage(violation: CountViolation): string {
  switch (violation.kind) {
    case 'REIMBURSABLE_EXCEEDS_SERVED':
      return `Reimbursable + non-reimbursable is ${violation.excess} more than served. Served must equal the two combined.`
    case 'SPLIT_BELOW_SERVED':
      return `Reimbursable + non-reimbursable is ${violation.shortfall} short of served. Served must equal the two combined.`
    case 'SERVED_EXCEEDS_PLANNED':
      return `Served is ${violation.excess} more than provisioned — fine if you served walk-ins or seconds.`
  }
}
